import * as React from 'react';
import { SiteSettingSiteAdminTelemetryEvent } from '~common/telemetryEvents/TelemetryEvents';
import { buildSiteSettingSiteAdminTelemetryEvent, getSiteSettingUUID } from '~common/telemetryEvents/util/SiteSettingTelemetryUtil';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { NavLocationSelectors } from '~selectors/NavigationStateSelectors';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { GeneralSiteSettingsSubspaceState } from '../../../app/spaces/SiteSettingsSpace/GeneralSiteSettingsSubspace';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { Sites } from '../../../ts/resources/Sites';
import { SettingsFormikContainer } from '../../components/common/SettingsFormikContainer';
import { ISiteAdminSettings, ISiteAdminSettingsWithSubscriptions, ISiteSettingsForSiteAdmin } from '../../types/ISiteSettingsForSiteAdmin';
import { useSelector } from '../utils';

/**
 * The settings passed here are really the top-level settings, but because we're
 * recursively passing the prop down, we have to keep it named "settings"
 */
export type ReactSiteSettingsForSiteAdminSubFormikProps = {
  settings: GeneralSiteSettingsSubspaceState;
};

const sections = [
  'SiteInviteNotificationSection',
  'UpdateSiteLogoSection',
  'StartPageSection',
  'UserVisibilitySection',
  'AskDataSection',
  'AutomaticAccessSection',
  'SensitiveLineageDataSection',
  'ExtractEncryptionSection',
  'MobileSection',
  'SupportAccessSection',
  'CollectionsSection',
  'SharingSection',
  'CommentingSettingSection',
  'DataAlertsSection',
  'SubscriptionsSection',
  'VizRecommendationsSection',
  'RequestAccessSection',
  'CrossDataBaseJoinsSection',
  'MetricsContentTypeSection',
  'SiteTimeZoneSection',
  'NotifySiteAdminsOnThrottleSection',
  'RunNowSection',
  'UserNotificationsSiteSettingsSection',
  'NotificationFlowRunsSection',
  'WebPageObjectsSection',
  'PersonalSpaceSection',
  'OAuthClientsRegistrySection',
];

export function ReactSiteSettingsForSiteAdmin(props: ReactSiteSettingsForSiteAdminSubFormikProps) {
  const { settings, ...rest } = props;
  const siteId = getSiteIdFromGeneralSettings(settings);

  const submitHandler = useSubmitHandler(settings);

  // Adapter to move subscription settings down a level with the rest of settings
  // instead of on its own tier
  const siteAdminSettingsWithSubscriptions: ISiteAdminSettingsWithSubscriptions = React.useMemo(() => ({
    ...settings.siteSettings.siteAdminSettings as ISiteAdminSettings,
    subscriptionSettings: settings.siteSettings.subscriptionSettings
  }), [settings?.siteSettings?.siteAdminSettings, settings?.siteSettings?.subscriptionSettings]);

  return (
    <SettingsFormikContainer
      formName={'ReactSiteSettingsForSiteAdmin'}
      {...rest}
      settings={siteAdminSettingsWithSubscriptions}
      siteId={siteId}
      sections={sections}
      onSubmit={submitHandler}
     />
  );
}

function useSubmitHandler(settings: GeneralSiteSettingsSubspaceState) {
  const isInSettingsLocation = useSelector(NavLocationSelectors.inSettingsLocation);
  return React.useCallback(async (newSiteAdminSettings: ISiteAdminSettingsWithSubscriptions) => {
    const oldSiteSettingsForSiteAdmin = settings.siteSettings;

    const siteId = getSiteIdFromGeneralSettings(settings);

    const sitesService = ServiceLocator.get<Sites>('Sites');
    const result: ISiteSettingsForSiteAdmin | IResult = await sitesService.updateSiteSettingsForSiteAdmin(
      siteId, newSiteAdminSettings, newSiteAdminSettings.subscriptionSettings
    );

    if (result?.errors?.length) {
      throw result.errors.map((e: any) => sitesService.errorMessage(e.code, null));
    } else {
      // TODO: Gate this behind certain use-cases
      if (!isInSettingsLocation) {
        return;
      }

      const newSiteSettingsForSiteAdmin = result as ISiteSettingsForSiteAdmin;

      // send siteAdminSettings setting for siteAdminSettings admin telemetry events
      if (shouldSendTelemetryForSite(newSiteAdminSettings as ISiteAdminSettings)
        && shouldSendTelemetryForSite(oldSiteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings)) {
        Telemetry.sendEvent<SiteSettingSiteAdminTelemetryEvent>(
          buildSiteSettingSiteAdminTelemetryEvent(
            newSiteSettingsForSiteAdmin,
            oldSiteSettingsForSiteAdmin,
            getSiteSettingUUID(true)
          )
        );
      }

      return {
        settings: {
          ...(newSiteSettingsForSiteAdmin.siteAdminSettings),
          subscriptionSettings: newSiteSettingsForSiteAdmin.subscriptionSettings
        }
      };
    }

  }, [isInSettingsLocation,
    settings?.siteSettings,
    settings?.siteSettings?.siteId,
    settings?.siteSettingsForServerAdmin?.id
  ]);
}

function getSiteIdFromGeneralSettings(settings: GeneralSiteSettingsSubspaceState) {
  const siteId = (settings.siteSettings as any).id
    ?? settings.siteSettings.siteId
    ?? settings?.siteSettingsForServerAdmin?.id;
  return siteId;
}

function shouldSendTelemetryForSite(siteAdminSettings: ISiteAdminSettings) {
  return siteAdminSettings && siteAdminSettings.siteNamedSharingSettings
    && siteAdminSettings.sitePersonalSpaceSettings && siteAdminSettings.askDataMode && siteAdminSettings.userVisibility;
}
